import {
  cn,
  DateTimePicker,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  RadioCardGroup,
  RadioCardItem,
  RichTextEditor,
} from '@eureca/ui';
import {
  ProgramEditShell,
  type ProgramEditShellProps,
  type ProgramEditStep,
  type ProgramStatus,
} from './ProgramEditShell';
import { BlockedEditTooltip } from './BlockedEditTooltip';
import type { ProgramAction } from '../program-actions-menu';
import { APP_TIMEZONE } from '@/lib/timezone';
import { UploadIcon, XIcon } from './icons';
import type { ProgramContractTypeOption } from '@/features/programs/program-create/ProgramCreatePage.component';

// Campo de capa EXIBIDO porém DESABILITADO (EU-240) — o upload de capa não está
// liberado neste momento, então o campo aparece em modo read-only nos dois modos
// (create/edit) para sinalizar que existe mas não é editável ainda.
const SHOW_COVER_FIELD = true;

export type { ProgramEditStep, ProgramStatus };

export interface ProgramEditValues {
  name: string;
  description: string;
  contractType: string;
  startDate: string;
  endDate: string;
  targetRegistrations: string;
}

export interface ProgramEditPageProps extends Omit<
  ProgramEditShellProps,
  'subtitle' | 'activeStep' | 'children'
> {
  /**
   * Read-only display of the parent company. Not part of `ProgramEditValues`
   * because the consultor cannot change which company a program belongs to —
   * the field is rendered as a disabled `Input`.
   */
  companyName: string;
  values: ProgramEditValues;
  /** Tipos do catálogo (RN-01) — já ordenados pelo rótulo. */
  contractTypeOptions: readonly ProgramContractTypeOption[];
  errors: Record<string, string>;
  coverFileName: string | null;
  /** Resolved URL for the cover preview thumb — pending file or saved program cover. */
  coverPreviewUrl: string | null;
  onFieldChange: (field: keyof ProgramEditValues, value: string) => void;
  onCoverChange: (file: File | null) => void;
  /** Passed through to ProgramEditShell for the header ellipsis menu. */
  onHeaderAction?: (action: ProgramAction) => void;
}

// EU-709 — a lista dos tipos vem do catálogo (RN-01), pela prop
// `contractTypeOptions`. Ver `ProgramCreatePage.component.tsx`.

export function ProgramEditPage({
  companyName,
  values,
  contractTypeOptions,
  errors,
  coverFileName,
  coverPreviewUrl,
  onFieldChange,
  onCoverChange,
  onHeaderAction,
  ...shellProps
}: ProgramEditPageProps) {
  // EU-570 — Encerrado/Cancelado: campos bloqueados + tooltip "Edição
  // bloqueada". O fieldset do shell desabilita os controles nativos; aqui só
  // os não-nativos (RichTextEditor/DateTimePicker/RadioCardGroup) levam
  // `disabled` explícito, e cada campo ganha o envelope do tooltip.
  const readOnly = shellProps.readOnly ?? false;
  return (
    <ProgramEditShell
      {...shellProps}
      subtitle="Informações gerais"
      activeStep="info"
      onHeaderAction={onHeaderAction}
    >
      <BlockedEditTooltip enabled={readOnly}>
        <FormField>
          <FormFieldLabel className="text-foreground">Empresa</FormFieldLabel>
          <Input
            value={companyName}
            readOnly
            disabled
            aria-readonly="true"
            className="cursor-not-allowed"
            data-testid="program-edit-company"
          />
        </FormField>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <FormField error={Boolean(errors.name)}>
          <FormFieldLabel className="text-foreground">Nome do programa</FormFieldLabel>
          <Input
            value={values.name}
            placeholder="Ex.: Programa Trilhas do Futuro"
            variant={errors.name ? 'destructive' : 'default'}
            onChange={(event) => onFieldChange('name', event.target.value)}
          />
          <FormFieldError>{errors.name}</FormFieldError>
        </FormField>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <FormField error={Boolean(errors.description)}>
          <FormFieldLabel className="text-foreground">Descrição do programa</FormFieldLabel>
          <RichTextEditor
            value={values.description}
            hasError={Boolean(errors.description)}
            disabled={readOnly}
            onChange={(html) => onFieldChange('description', html)}
          />
          <FormFieldError>{errors.description}</FormFieldError>
        </FormField>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <FormField error={Boolean(errors.contractType)}>
          {/* EU-709 RN-01 — o campo é `Tipo de programa`, espelhado com o
                  `Tipo de vaga`. O rótulo antigo ("Tipo de contratação") era o
                  nome interno da tabela de referência, não o do produto. */}
          <FormFieldLabel className="text-foreground">Tipo de programa</FormFieldLabel>
          <RadioCardGroup
            value={values.contractType}
            disabled={readOnly}
            onValueChange={(value) => onFieldChange('contractType', value)}
          >
            {contractTypeOptions.map((option) => (
              <RadioCardItem key={option.value} value={option.value} className="group px-4 py-4">
                {option.label}
              </RadioCardItem>
            ))}
          </RadioCardGroup>
          <FormFieldError>{errors.contractType}</FormFieldError>
        </FormField>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <div className="flex flex-col gap-5 sm:flex-row">
          <FormField error={Boolean(errors.startDate)} className="flex-1">
            <FormFieldLabel className="text-foreground">Início das inscrições</FormFieldLabel>
            <DateTimePicker
              value={values.startDate}
              timeZone={APP_TIMEZONE}
              hasError={Boolean(errors.startDate)}
              disabled={readOnly}
              onChange={(value) => onFieldChange('startDate', value)}
            />
            <FormFieldError>{errors.startDate}</FormFieldError>
          </FormField>

          <FormField error={Boolean(errors.endDate)} className="flex-1">
            <FormFieldLabel className="text-foreground">Fim das inscrições</FormFieldLabel>
            <DateTimePicker
              value={values.endDate}
              timeZone={APP_TIMEZONE}
              defaultTime="23:59"
              hasError={Boolean(errors.endDate)}
              disabled={readOnly}
              onChange={(value) => onFieldChange('endDate', value)}
            />
            <FormFieldError>{errors.endDate}</FormFieldError>
          </FormField>
        </div>
      </BlockedEditTooltip>

      <BlockedEditTooltip enabled={readOnly}>
        <FormField error={Boolean(errors.targetRegistrations)}>
          <FormFieldLabel className="text-foreground">Meta de inscritos do programa</FormFieldLabel>
          <Input
            value={values.targetRegistrations}
            placeholder="Ex.: 10000"
            inputMode="numeric"
            pattern="[0-9]*"
            variant={errors.targetRegistrations ? 'destructive' : 'default'}
            // Strip non-digits at the input boundary so paste/autofill never
            // sneak letters into the form value. Zod's `\d+` regex is the
            // safety net for programmatic writes.
            onChange={(event) =>
              onFieldChange('targetRegistrations', event.target.value.replace(/\D/g, ''))
            }
          />
          <FormFieldError>{errors.targetRegistrations}</FormFieldError>
        </FormField>
      </BlockedEditTooltip>

      {/* Seção "Capa do programa" EXIBIDA porém DESABILITADA (EU-240). */}
      {SHOW_COVER_FIELD ? (
        <BlockedEditTooltip enabled={readOnly}>
          <FormField error={Boolean(errors.cover)}>
            <FormFieldLabel className="text-foreground">Capa do programa</FormFieldLabel>
            <CoverFileInput
              fileName={coverFileName}
              previewUrl={coverPreviewUrl}
              hasError={Boolean(errors.cover)}
              onChange={onCoverChange}
              disabled
            />
            <FormFieldHint>
              Envie em .JPG ou .PNG, com no máximo 5 MB e com aspect ratio de 16 : 9.
            </FormFieldHint>
            <FormFieldError>{errors.cover}</FormFieldError>
          </FormField>
        </BlockedEditTooltip>
      ) : null}
    </ProgramEditShell>
  );
}

interface CoverFileInputProps {
  fileName: string | null;
  /** When set, renders a 16:9 preview thumbnail above the picker with X-on-hover. */
  previewUrl: string | null;
  hasError: boolean;
  onChange: (file: File | null) => void;
  /** Read-only mode — picker is greyed/non-interactive and the remove button is hidden. */
  disabled?: boolean;
}

function CoverFileInput({
  fileName,
  previewUrl,
  hasError,
  onChange,
  disabled = false,
}: CoverFileInputProps) {
  const label = fileName
    ? 'Clique para selecionar outro arquivo'
    : 'Clique para selecionar um arquivo';
  return (
    <div className="flex flex-col gap-2">
      {previewUrl && (
        <div
          className={cn(
            'group relative w-full overflow-hidden rounded-md border bg-muted',
            // 16:9 — matches what the validators enforce.
            'aspect-video',
            hasError ? 'border-destructive' : 'border-input'
          )}
        >
          <img
            src={previewUrl}
            alt="Pré-visualização da capa do programa"
            className="size-full object-cover"
            data-testid="program-edit-cover-preview"
          />
          {!disabled && (
            <button
              type="button"
              className={cn(
                'absolute top-2 right-2 flex size-7 items-center justify-center rounded-full',
                'bg-background/90 text-foreground shadow-2xs ring-1 ring-input',
                'opacity-0 transition-opacity duration-150',
                'group-hover:opacity-100 focus-visible:opacity-100',
                'hover:bg-destructive hover:text-destructive-foreground hover:ring-destructive'
              )}
              aria-label="Remover capa"
              onClick={() => onChange(null)}
              data-testid="program-edit-cover-remove"
            >
              <XIcon className="size-4" aria-hidden />
            </button>
          )}
        </div>
      )}
      <label
        aria-disabled={disabled}
        className={cn(
          'flex h-9 w-full flex-row items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-2xs transition-colors',
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer focus-within:ring-2 hover:bg-muted/30',
          hasError
            ? 'border-destructive focus-within:ring-destructive/40'
            : 'border-input focus-within:ring-primary/40'
        )}
      >
        <UploadIcon className="size-4 text-foreground" />
        <span className="flex-1 truncate font-medium text-foreground">{label}</span>
        {fileName && (
          <span className="truncate text-sm font-normal text-muted-foreground">{fileName}</span>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png"
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            onChange(file);
          }}
        />
      </label>
    </div>
  );
}
